import React, { useState, useEffect } from 'react';
import { FiUserCheck, FiDownload, FiSearch } from 'react-icons/fi';
import { results as resultsApi, exams as examsApi } from '../../api';
import toast from 'react-hot-toast';

export function ShortlistPage() {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [mode, setMode] = useState('topN');
  const [topN, setTopN] = useState(10);
  const [minScore, setMinScore] = useState(70);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { examsApi.getAll({ limit: 100 }).then(r => setExams(r.data.data || [])); }, []);

  const handleGenerate = async () => {
    if (!examId) { toast.error('Select an exam'); return; }
    setLoading(true);
    try {
      const payload = { examId, format: 'json' };
      if (mode === 'topN') payload.topN = topN;
      else payload.minScore = minScore;
      const res = await resultsApi.shortlist(payload);
      setCandidates(res.data.data || []);
      if (!res.data.data?.length) toast.error('No candidates match criteria');
    } catch { toast.error('Failed to generate shortlist'); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    if (!examId) { toast.error('Generate shortlist first'); return; }
    try {
      const payload = { examId, format: 'excel' };
      if (mode === 'topN') payload.topN = topN; else payload.minScore = minScore;
      const res = await resultsApi.shortlistExcel(payload);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `shortlist_${Date.now()}.xlsx`; a.click();
      toast.success('Shortlist exported');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="text-2xl font-black text-gray-900">Shortlist Candidates</h1>
        <p className="text-gray-500 text-sm">Filter and export top performing candidates</p>
      </div>
      <div className="card p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label">Select Exam</label>
            <select className="input" value={examId} onChange={e => { setExamId(e.target.value); setCandidates([]); }}>
              <option value="">Choose exam...</option>
              {exams.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Criteria</label>
            <select className="input" value={mode} onChange={e => setMode(e.target.value)}>
              <option value="topN">Top N Candidates</option>
              <option value="minScore">Minimum Score %</option>
            </select>
          </div>
          <div>
            <label className="label">{mode === 'topN' ? 'Number of Candidates' : 'Minimum Score (%)'}</label>
            <input type="number" className="input" min={1} max={mode === 'topN' ? 1000 : 100}
              value={mode === 'topN' ? topN : minScore}
              onChange={e => mode === 'topN' ? setTopN(parseInt(e.target.value)) : setMinScore(parseFloat(e.target.value))} />
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={handleGenerate} disabled={loading} className="btn-primary">
            {loading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiUserCheck size={15} />}
            Generate Shortlist
          </button>
          {candidates.length > 0 && (
            <button onClick={handleExport} className="btn-secondary"><FiDownload size={15} /> Export Excel</button>
          )}
        </div>
      </div>

      {candidates.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-bold text-gray-900">{candidates.length} Shortlisted Candidates</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b">
                <th className="table-th">#</th><th className="table-th">Name</th><th className="table-th">Email</th>
                <th className="table-th">Score</th><th className="table-th">Percentage</th><th className="table-th">Rank</th>
              </tr></thead>
              <tbody>
                {candidates.map((c, i) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    <td className="table-td text-gray-400 text-xs">{i + 1}</td>
                    <td className="table-td font-semibold">{c.first_name} {c.last_name}</td>
                    <td className="table-td text-gray-500 text-sm">{c.email}</td>
                    <td className="table-td font-bold">{parseFloat(c.raw_score).toFixed(1)}</td>
                    <td className="table-td">
                      <span className="font-bold text-emerald-600">{parseFloat(c.percentage).toFixed(1)}%</span>
                    </td>
                    <td className="table-td">{c.rank ? `#${c.rank}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ShortlistPage;
